import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";

const SUBJECT_COLORS = {
    Odia: "#F43F5E",
    English: "#6366F1",
    Mathematics: "#2563EB",
    EVS: "#10B981",
    Science: "#A855F7",
    "Social Science": "#F59E0B",
};

export default function Analytics() {
    const { t, subjectLabel, lang } = useApp();
    const [stats, setStats] = useState(null);

    useEffect(() => { api.stats().then(setStats); }, []);
    if (!stats) return <div className="max-w-5xl mx-auto px-4 py-12 text-center text-muted-foreground">{t.loading}</div>;

    const byClass = (stats.by_class || []).map((c) => ({ name: `${t.classN(c.class)}`, count: c.count }));
    const bySubject = (stats.by_subject || []).map((s) => ({ name: subjectLabel(s.subject), value: s.count, raw: s.subject }));

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:pb-32">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">{t.analytics}</p>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t.analytics}</h1>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6">
                <Stat label={t.totalClasses} value={stats.total_classes} accent="from-primary to-secondary" />
                <Stat label={t.totalSubjects} value={stats.total_subjects} accent="from-accent to-emerald-500" />
                <Stat label={t.totalLOCodes} value={stats.total_codes} accent="from-amber-500 to-rose-500" className="col-span-2 md:col-span-1" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div data-testid="chart-by-class" className="rounded-3xl bg-card border border-border/70 p-5 shadow-sm">
                    <div className="font-display font-bold text-lg mb-1">LO Codes per Class</div>
                    <div className="text-xs text-muted-foreground mb-4">Bar Chart</div>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={byClass}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                            <Bar dataKey="count" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div data-testid="chart-by-subject" className="rounded-3xl bg-card border border-border/70 p-5 shadow-sm">
                    <div className="font-display font-bold text-lg mb-1">Subject Distribution</div>
                    <div className="text-xs text-muted-foreground mb-4">Pie Chart</div>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={bySubject} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={50} paddingAngle={3}>
                                {bySubject.map((entry, i) => (
                                    <Cell key={i} fill={SUBJECT_COLORS[entry.raw] || "#64748B"} />
                                ))}
                            </Pie>
                            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div data-testid="chart-line-class" className="rounded-3xl bg-card border border-border/70 p-5 shadow-sm lg:col-span-2">
                    <div className="font-display font-bold text-lg mb-1">LO Growth across Classes</div>
                    <div className="text-xs text-muted-foreground mb-4">Line Chart</div>
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={byClass}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                            <Line type="monotone" dataKey="count" stroke="hsl(var(--accent))" strokeWidth={3} dot={{ r: 5 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, accent, className = "" }) {
    return (
        <div className={`relative overflow-hidden rounded-3xl bg-card border border-border/70 p-5 ${className}`}>
            <div className={`absolute -top-6 -right-6 h-24 w-24 rounded-full bg-gradient-to-br ${accent} opacity-20 blur-2xl`} />
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">{label}</div>
            <div className="font-display text-3xl sm:text-4xl font-extrabold">{Number(value).toLocaleString()}</div>
        </div>
    );
}
